import { useState } from "react";
import useReveal from "@/hooks/useReveal";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, MapPin, Loader2, Send, Check } from "lucide-react";

export default function ContactPage() {
  const ref = useReveal();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await submitContact(form);
      toast.success(data.message);
      setDone(true);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your inputs");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main ref={ref} data-testid="contact-page" className="pt-12 bg-white">
      <section className="relative py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          {/* Left */}
          <div className="lg:col-span-5 reveal">
            <span className="font-display text-[11px] uppercase tracking-[0.25em] text-[#5FA836] font-bold">
              Contact
            </span>
            <h1 className="mt-4 font-display text-5xl md:text-7xl font-black tracking-[-0.03em] leading-[0.95] text-[#24303F]">
              Say hello. <br />
              <span className="text-gray-400">We listen.</span>
            </h1>
            <p className="mt-7 font-body text-gray-600 text-base md:text-lg leading-relaxed max-w-md">
              Have a question, a partnership idea, or just want to be a launch
              city? Drop us a line — we read every message.
            </p>

            <div className="mt-10 space-y-5">
              <ContactRow icon={MessageCircle} label="Support" value="support@ridemateapp.in" />
              <ContactRow icon={MapPin} label="HQ" value="Varanasi, India" />
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7 reveal">
            <div className="rounded-3xl border border-[#e5e7eb] bg-[#F9FAFB] p-7 md:p-10">
              {done ? (
                <div data-testid="contact-success" className="py-10 text-center">
                  <div className="mx-auto h-14 w-14 rounded-full bg-[#5FA836] grid place-items-center">
                    <Check className="w-7 h-7 text-white" strokeWidth={3} />
                  </div>
                  <h3 className="mt-6 font-display text-3xl font-bold text-[#24303F]">
                    Message received.
                  </h3>
                  <p className="mt-3 font-body text-gray-600 max-w-md mx-auto">
                    Our team will get back to you within 24 hours. In the meantime,
                    don't forget to join the waitlist for ₹100 cashback.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="grid gap-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      testid="contact-name"
                      label="Your name"
                      value={form.name}
                      onChange={onChange("name")}
                      required
                      minLength={2}
                    />
                    <Field
                      testid="contact-email"
                      label="Email"
                      type="email"
                      value={form.email}
                      onChange={onChange("email")}
                      required
                    />
                  </div>
                  <Field
                    testid="contact-subject"
                    label="Subject"
                    value={form.subject}
                    onChange={onChange("subject")}
                    placeholder="What's this about?"
                  />
                  <div>
                    <label className="font-display text-[11px] uppercase tracking-[0.2em] text-gray-500 font-bold">
                      Message
                    </label>
                    <textarea
                      data-testid="contact-message"
                      required
                      minLength={5}
                      rows={6}
                      placeholder="Tell us a bit more…"
                      value={form.message}
                      onChange={onChange("message")}
                      className="mt-2 w-full rounded-2xl border border-[#e5e7eb] bg-white px-4 py-3 font-body text-base outline-none focus:border-[#5FA836]/60 text-[#24303F] placeholder:text-gray-400 resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={loading}
                    className="btn-primary rounded-xl px-6 py-3.5 inline-flex items-center justify-center gap-2 disabled:opacity-60 w-full sm:w-fit"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        Send message <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

const Field = ({ testid, label, ...props }) => (
  <div>
    <label className="font-display text-[11px] uppercase tracking-[0.2em] text-gray-500 font-bold">
      {label}
    </label>
    <input
      data-testid={testid}
      {...props}
      className="mt-2 w-full rounded-xl border border-[#e5e7eb] bg-white px-4 py-3 font-body text-base outline-none focus:border-[#5FA836]/60 text-[#24303F] placeholder:text-gray-400"
    />
  </div>
);

const ContactRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-4">
    <div className="h-10 w-10 rounded-xl bg-[#5FA836]/10 grid place-items-center text-[#5FA836] shrink-0">
      <Icon className="w-4 h-4" />
    </div>
    <div>
      <div className="font-display text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold">
        {label}
      </div>
      <div className="font-body text-base text-[#24303F] mt-1 font-semibold">{value}</div>
    </div>
  </div>
);
